'use client';

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

type DaltonismMode = 'none' | 'protanopia' | 'deuteranopia' | 'tritanopia';

interface AccessibilitySettings {
  largeFonts: boolean;
  highContrast: boolean;
  daltonismMode: DaltonismMode;
  reducedMotion: boolean;
  focusVisible: boolean;
  darkMode: boolean;
  screenReaderMode: boolean;
}

interface AccessibilityContextValue {
  settings: AccessibilitySettings;
  toggleLargeFonts: () => void;
  toggleHighContrast: () => void;
  setDaltonismMode: (mode: DaltonismMode) => void;
  toggleReducedMotion: () => void;
  toggleFocusVisible: () => void;
  toggleDarkMode: () => void;
  toggleScreenReaderMode: () => void;
  resetSettings: () => void;
  activeCount: number;
}

const defaultSettings: AccessibilitySettings = {
  largeFonts: false,
  highContrast: false,
  daltonismMode: 'none',
  reducedMotion: false,
  focusVisible: false,
  darkMode: false,
  screenReaderMode: false,
};

const STORAGE_KEY = 'utss_accessibility';

const AccessibilityContext = createContext<AccessibilityContextValue | null>(null);

export function useAccessibility() {
  const ctx = useContext(AccessibilityContext);
  if (!ctx) {
    throw new Error('useAccessibility must be used within AccessibilityProvider');
  }
  return ctx;
}

// SVG filter definitions for colorblind simulation
function ColorBlindFilters() {
  return (
    <svg
      style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}
      aria-hidden="true"
    >
      <defs>
        {/* Protanopia (red-blind) */}
        <filter id="protanopia-filter">
          <feColorMatrix
            type="matrix"
            values="0.567, 0.433, 0, 0, 0
                    0.558, 0.442, 0, 0, 0
                    0, 0.242, 0.758, 0, 0
                    0, 0, 0, 1, 0"
          />
        </filter>
        {/* Deuteranopia (green-blind) */}
        <filter id="deuteranopia-filter">
          <feColorMatrix
            type="matrix"
            values="0.625, 0.375, 0, 0, 0
                    0.7, 0.3, 0, 0, 0
                    0, 0.3, 0.7, 0, 0
                    0, 0, 0, 1, 0"
          />
        </filter>
        {/* Tritanopia (blue-blind) */}
        <filter id="tritanopia-filter">
          <feColorMatrix
            type="matrix"
            values="0.95, 0.05, 0, 0, 0
                    0, 0.433, 0.567, 0, 0
                    0, 0.475, 0.525, 0, 0
                    0, 0, 0, 1, 0"
          />
        </filter>
      </defs>
    </svg>
  );
}

export function AccessibilityProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<AccessibilitySettings>(defaultSettings);
  const [mounted, setMounted] = useState(false);

  // Load from localStorage + detect system preferences on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        setSettings({ ...defaultSettings, ...JSON.parse(stored) });
      } else {
        // Detect system preferences for first-time users
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const prefersHighContrast = window.matchMedia('(prefers-contrast: more)').matches;

        if (prefersDark || prefersReducedMotion || prefersHighContrast) {
          setSettings(prev => ({
            ...prev,
            darkMode: prefersDark,
            reducedMotion: prefersReducedMotion,
            highContrast: prefersHighContrast,
          }));
        }
      }
    } catch {
      // Ignore parse errors
    }
    setMounted(true);
  }, []);

  // Persist to localStorage on change
  useEffect(() => {
    if (!mounted) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {
      // Ignore storage errors
    }
  }, [settings, mounted]);

  // Apply CSS classes to <html>
  useEffect(() => {
    if (!mounted) return;
    const root = document.documentElement;

    const toggleClass = (className: string, active: boolean) => {
      root.classList.toggle(className, active);
    };

    toggleClass('accessibility-large-fonts', settings.largeFonts);
    toggleClass('accessibility-high-contrast', settings.highContrast);
    toggleClass('accessibility-reduced-motion', settings.reducedMotion);
    toggleClass('accessibility-focus-visible', settings.focusVisible);
    toggleClass('dark', settings.darkMode);
    toggleClass('accessibility-screen-reader', settings.screenReaderMode);

    // Daltonism: remove all then add active one
    root.classList.remove(
      'accessibility-protanopia',
      'accessibility-deuteranopia',
      'accessibility-tritanopia',
    );
    if (settings.daltonismMode !== 'none') {
      root.classList.add(`accessibility-${settings.daltonismMode}`);
    }
  }, [settings, mounted]);

  // Listen for system preference changes
  useEffect(() => {
    if (!mounted) return;

    const darkQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    const handleDarkChange = (e: MediaQueryListEvent) => {
      // Only auto-apply if user hasn't explicitly set it
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        setSettings(prev => ({ ...prev, darkMode: e.matches }));
      }
    };

    const handleMotionChange = (e: MediaQueryListEvent) => {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        setSettings(prev => ({ ...prev, reducedMotion: e.matches }));
      }
    };

    darkQuery.addEventListener('change', handleDarkChange);
    motionQuery.addEventListener('change', handleMotionChange);

    return () => {
      darkQuery.removeEventListener('change', handleDarkChange);
      motionQuery.removeEventListener('change', handleMotionChange);
    };
  }, [mounted]);

  const toggleLargeFonts = useCallback(() => {
    setSettings(prev => ({ ...prev, largeFonts: !prev.largeFonts }));
  }, []);

  const toggleHighContrast = useCallback(() => {
    setSettings(prev => ({ ...prev, highContrast: !prev.highContrast }));
  }, []);

  const setDaltonismMode = useCallback((mode: DaltonismMode) => {
    setSettings(prev => ({ ...prev, daltonismMode: mode }));
  }, []);

  const toggleReducedMotion = useCallback(() => {
    setSettings(prev => ({ ...prev, reducedMotion: !prev.reducedMotion }));
  }, []);

  const toggleFocusVisible = useCallback(() => {
    setSettings(prev => ({ ...prev, focusVisible: !prev.focusVisible }));
  }, []);

  const toggleDarkMode = useCallback(() => {
    setSettings(prev => ({ ...prev, darkMode: !prev.darkMode }));
  }, []);

  const toggleScreenReaderMode = useCallback(() => {
    setSettings(prev => ({ ...prev, screenReaderMode: !prev.screenReaderMode }));
  }, []);

  const resetSettings = useCallback(() => {
    setSettings(defaultSettings);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Ignore
    }
  }, []);

  const activeCount = [
    settings.largeFonts,
    settings.highContrast,
    settings.daltonismMode !== 'none',
    settings.reducedMotion,
    settings.focusVisible,
    settings.darkMode,
    settings.screenReaderMode,
  ].filter(Boolean).length;

  return (
    <AccessibilityContext.Provider
      value={{
        settings,
        toggleLargeFonts,
        toggleHighContrast,
        setDaltonismMode,
        toggleReducedMotion,
        toggleFocusVisible,
        toggleDarkMode,
        toggleScreenReaderMode,
        resetSettings,
        activeCount,
      }}
    >
      <ColorBlindFilters />
      {children}
    </AccessibilityContext.Provider>
  );
}
